'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import AppImage from '@/components/ui/AppImage';
import type { ReviewEntry } from '@/lib/content-types';
import { useLanguage } from '@/components/language/LanguageProvider';
import { translateReviewQuote } from '@/lib/i18n';

interface StatsSectionProps {
  reviews: ReviewEntry[];
}

export default function StatsSection({ reviews }: StatsSectionProps) {
  const revealRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [prevIdx, setPrevIdx] = useState<number | null>(null);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [transitioning, setTransitioning] = useState(false);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transitionDuration = 500; // ms
  const testimonials = reviews;
  const hasTestimonials = testimonials.length > 0;
  const { dictionary, language } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const goTo = useCallback(
    (idx: number, dir: 'next' | 'prev') => {
      if (transitioning || idx === activeIdx) return;
      setDirection(dir);
      setPrevIdx(activeIdx);
      setActiveIdx(idx);
      setTransitioning(true);
      setTimeout(() => {
        setPrevIdx(null);
        setTransitioning(false);
      }, transitionDuration);
    },
    [transitioning, activeIdx]
  );

  const prev = useCallback(() => {
    if (!hasTestimonials) return;
    const idx = (activeIdx - 1 + testimonials.length) % testimonials.length;
    goTo(idx, 'prev');
  }, [activeIdx, goTo, hasTestimonials, testimonials.length]);

  const next = useCallback(() => {
    if (!hasTestimonials) return;
    const idx = (activeIdx + 1) % testimonials.length;
    goTo(idx, 'next');
  }, [activeIdx, goTo, hasTestimonials, testimonials.length]);

  const resetAutoPlay = useCallback(() => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    if (!hasTestimonials) return;
    autoPlayRef.current = setInterval(() => {
      setDirection('next');
      setActiveIdx((i) => {
        const nextI = (i + 1) % testimonials.length;
        setPrevIdx(i);
        setTransitioning(true);
        setTimeout(() => {
          setPrevIdx(null);
          setTransitioning(false);
        }, transitionDuration);
        return nextI;
      });
    }, 5000);
  }, [hasTestimonials, testimonials.length]);

  useEffect(() => {
    resetAutoPlay();
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [resetAutoPlay]);

  const handlePrev = useCallback(() => {
    prev();
    resetAutoPlay();
  }, [prev, resetAutoPlay]);
  const handleNext = useCallback(() => {
    next();
    resetAutoPlay();
  }, [next, resetAutoPlay]);
  const handleDot = useCallback(
    (i: number) => {
      const dir = i > activeIdx ? 'next' : 'prev';
      goTo(i, dir);
      resetAutoPlay();
    },
    [activeIdx, goTo, resetAutoPlay]
  );

  // Slide offset values
  const enterFrom = direction === 'next' ? '40px' : '-40px';
  const exitTo = direction === 'next' ? '-40px' : '40px';

  const renderCard = (idx: number, isActive: boolean) => {
    const t = testimonials[idx];
    const style: React.CSSProperties = isActive
      ? {
          position: 'absolute',
          inset: 0,
          opacity: transitioning ? 1 : 1,
          transform: 'translateX(0)',
          transition: `opacity ${transitionDuration}ms cubic-bezier(0.4,0,0.2,1), transform ${transitionDuration}ms cubic-bezier(0.4,0,0.2,1)`,
          animation: transitioning
            ? `carouselEnter ${transitionDuration}ms cubic-bezier(0.4,0,0.2,1) forwards`
            : 'none',
          zIndex: 2,
        }
      : {
          position: 'absolute',
          inset: 0,
          opacity: 0,
          transform: `translateX(${exitTo})`,
          transition: `opacity ${transitionDuration}ms cubic-bezier(0.4,0,0.2,1), transform ${transitionDuration}ms cubic-bezier(0.4,0,0.2,1)`,
          zIndex: 1,
          pointerEvents: 'none',
        };

    return (
      <div key={idx} style={style} className="h-full flex flex-col md:flex-row gap-0">
        {/* Tattoo image */}
        <div className="relative w-full md:w-48 h-44 sm:h-48 md:h-full flex-shrink-0 overflow-hidden">
          <AppImage
            src={t.tattooImage}
            alt={t.tattooAlt}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 192px"
          />
          <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-transparent to-[#111111]" />
        </div>

        {/* Review text */}
        <div className="flex-1 p-5 sm:p-6 md:p-8 flex flex-col h-full">
          {/* Stars */}
          <div className="flex-1 min-h-0">
            <div className="flex gap-1 mb-4 sm:mb-5">
              {Array.from({ length: t.rating }).map((_, i) => (
                <svg
                  key={i}
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="#C8A96E"
                  stroke="none"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              ))}
              {Array.from({ length: Math.max(0, 5 - t.rating) }).map((_, i) => (
                <svg
                  key={`empty-${i}`}
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="#363636"
                  stroke="none"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              ))}
            </div>
            <blockquote
              className="text-foreground/75 text-[15px] sm:text-base leading-[1.75] mb-5 sm:mb-6 font-medium italic overflow-hidden"
              title={translateReviewQuote(t.quote, language)}
              style={{ fontFamily: 'var(--font-fraunces)' }}
            >
              &ldquo;{translateReviewQuote(t.quote, language)}&rdquo;
            </blockquote>
          </div>

          <div className="mt-auto flex items-center justify-between flex-wrap gap-3 sm:gap-4">
            {/* Author */}
            <div className="flex items-center gap-3 pt-4 sm:pt-5 border-t border-border flex-1">
              <div className="w-9 h-9 rounded-full bg-primary/15 border border-primary/25 flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-bold text-xs">{t.initials}</span>
              </div>
              <div>
                <div className="text-foreground font-bold text-sm">{t.name}</div>
                <div className="text-muted-foreground text-xs mt-0.5">{t.detail}</div>
              </div>
            </div>

            {/* Navigation controls */}
            <div className="flex items-center gap-2 sm:gap-3 pt-4 sm:pt-5">
              <button
                onClick={handlePrev}
                className="w-9 h-9 rounded-full border border-border hover:border-primary/40 flex items-center justify-center text-foreground/50 hover:text-foreground transition-all duration-200"
                aria-label={dictionary.stats.prevReview}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <div className="flex gap-1.5">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handleDot(i)}
                    className={`rounded-full transition-all duration-300 ${
                      i === activeIdx
                        ? 'w-5 h-2 bg-primary'
                        : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                    }`}
                    aria-label={`${dictionary.stats.goToReview} ${i + 1}`}
                  />
                ))}
              </div>
              <button
                onClick={handleNext}
                className="w-9 h-9 rounded-full border border-border hover:border-primary/40 flex items-center justify-center text-foreground/50 hover:text-foreground transition-all duration-200"
                aria-label={dictionary.stats.nextReview}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="relative bg-[#0D0D0D] py-24 overflow-hidden">
      {/* Atmospheric blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/6 rounded-full blur-[140px] pointer-events-none translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary/4 rounded-full blur-[120px] pointer-events-none -translate-x-1/3 translate-y-1/3" />
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to right, #C8A96E 1px, transparent 1px), linear-gradient(to bottom, #C8A96E 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <div
          ref={(el) => {
            revealRefs.current[0] = el;
          }}
          className="reveal mb-16 md:mb-20"
        >
          <span className="section-label">{dictionary.stats.sectionLabel}</span>
          <h2
            className="text-4xl md:text-5xl font-black text-foreground tracking-tight leading-[1.05] max-w-xl"
            style={{ fontFamily: 'var(--font-fraunces)' }}
          >
            {dictionary.stats.title}
          </h2>
        </div>

        {/* Reviews Carousel */}
        <div
          ref={(el) => {
            revealRefs.current[1] = el;
          }}
          className="reveal reveal-delay-1"
        >
          <div className="relative bg-[#111111] border border-border rounded-3xl overflow-hidden">
            {/* Fixed-height container so cards stack absolutely */}
            <div className="relative h-[520px] sm:h-[500px] md:h-[290px]">
              <style>{`
                @keyframes carouselEnter {
                  from {
                    opacity: 0;
                    transform: translateX(${enterFrom});
                  }
                  to {
                    opacity: 1;
                    transform: translateX(0);
                  }
                }
              `}</style>
              {prevIdx !== null && renderCard(prevIdx, false)}
              {hasTestimonials ? (
                renderCard(activeIdx, true)
              ) : (
                <div className="p-8">
                  <p className="text-muted-foreground">{dictionary.stats.emptyReviews}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
